/**
 * Pure maths behind the ready-agents carousel and the category chip scroller: scroll edges, which cards
 * are in view, where a key or an arrow button should go, and where a drag should settle. No React, no
 * DOM, so it is unit-testable in isolation.
 */

/** Sub-pixel slack: scroll positions are fractional on zoomed and high-DPI screens. */
const EDGE_EPSILON = 2;

/** Mouse movement (px) before a press counts as a drag rather than a click. */
const DRAG_THRESHOLD = 6;

export type ScrollEdges = {
  atStart: boolean;
  atEnd: boolean;
  overflowing: boolean;
};

export function scrollEdges(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
): ScrollEdges {
  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  return {
    atStart: scrollLeft <= EDGE_EPSILON,
    atEnd: scrollLeft >= maxScroll - EDGE_EPSILON,
    overflowing: maxScroll > EDGE_EPSILON,
  };
}

export type CarouselGeometry = {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
  /** Width of one card. */
  itemWidth: number;
  /** Space between two cards. */
  gap: number;
  count: number;
};

/** How many whole cards fit in the viewport (at least 1, at most count). */
export function perViewOf(
  clientWidth: number,
  itemWidth: number,
  gap: number,
  count: number,
): number {
  if (count <= 0) return 0;
  const stride = itemWidth + gap;
  if (stride <= 0) return 1;
  const fit = Math.floor((clientWidth + gap + EDGE_EPSILON) / stride);
  return Math.min(count, Math.max(1, fit));
}

/** Highest index a carousel can start from while still filling the viewport. */
export function maxStartIndex(count: number, perView: number): number {
  return Math.max(0, count - Math.max(1, perView));
}

/** Zero-based inclusive range of the cards in view; null for an empty list. */
export function visibleRange(
  g: CarouselGeometry,
): { first: number; last: number } | null {
  if (g.count <= 0) return null;
  const perView = perViewOf(g.clientWidth, g.itemWidth, g.gap, g.count);
  const stride = g.itemWidth + g.gap;
  const edges = scrollEdges(g.scrollLeft, g.scrollWidth, g.clientWidth);
  if (edges.atEnd && edges.overflowing) {
    return { first: maxStartIndex(g.count, perView), last: g.count - 1 };
  }
  const first = clamp(
    stride > 0 ? Math.round(g.scrollLeft / stride) : 0,
    0,
    g.count - 1,
  );
  return { first, last: Math.min(g.count - 1, first + perView - 1) };
}

/** One card back (-1) or forward (+1), clamped so the viewport stays full. */
export function stepIndex(
  first: number,
  direction: -1 | 1,
  count: number,
  perView: number,
): number {
  return clamp(first + direction, 0, maxStartIndex(count, perView));
}

/** Target card for a carousel key; null when the key is not one the carousel handles. */
export function carouselKeyTarget(
  first: number,
  key: string,
  count: number,
  perView: number,
): number | null {
  if (count <= 0) return null;
  switch (key) {
    case "ArrowRight":
      return stepIndex(first, 1, count, perView);
    case "ArrowLeft":
      return stepIndex(first, -1, count, perView);
    case "Home":
      return 0;
    case "End":
      return maxStartIndex(count, perView);
    default:
      return null;
  }
}

/** Card a drag should settle on once released. */
export function nearestIndex(
  scrollLeft: number,
  stride: number,
  count: number,
  perView: number,
): number {
  if (count <= 0 || stride <= 0) return 0;
  return clamp(
    Math.round(scrollLeft / stride),
    0,
    maxStartIndex(count, perView),
  );
}

/** True once the pointer has moved far enough to be a drag and not a click. */
export function isDragGesture(
  dx: number,
  dy: number,
  threshold: number = DRAG_THRESHOLD,
): boolean {
  return Math.hypot(dx, dy) > threshold;
}

/** scrollLeft that centres an item in the viewport, clamped to the scrollable range. */
export function centeredScrollLeft(
  itemLeft: number,
  itemWidth: number,
  clientWidth: number,
  scrollWidth: number,
): number {
  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  return clamp(itemLeft - (clientWidth - itemWidth) / 2, 0, maxScroll);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
